/**
 * CommunityDashboard - Per-community dashboard showing KPIs, charts, and quick actions.
 *
 * Route: /communities/:id/dashboard
 *
 * When no datasets have been uploaded yet, shows an empty state with
 * guidance to upload data. Once data exists, shows KPIs and charts.
 *
 * Community data is received via Outlet context from CommunityLayout.
 */

import React, { useState, useMemo } from 'react'
import { useOutletContext, Link, useParams } from 'react-router-dom'
import { Row, Col, Card, Badge, Button } from 'react-bootstrap'
import { useTranslation, Trans } from 'react-i18next'
import EnergyBalanceChart from '../components/charts/EnergyBalanceChart'
import TariffProfileChart from '../components/charts/TariffProfileChart'
import MonthlyBillsChart from '../components/charts/MonthlyBillsChart'
import {
  communityKPIs,
  getCountryDefaults,
} from '../data/czechEnergyProfiles'
import {
  useCommunityTariffProfile,
  useCommunityEnergyBalance,
  useCommunityMonthlyBills,
} from '../hooks/useDatasets'

function CommunityDashboard() {
  const { community } = useOutletContext()
  const { id } = useParams()
  const { t } = useTranslation()

  // Energy balance chart scope state
  const [balanceScope, setBalanceScope] = useState('community')

  // Derive community summary from API data
  const buildings = community?.buildings || []
  const communityAssets = community?.community_assets || []
  const communityDatasets = community?.community_datasets || community?.datasets || []

  const totalBuildings = buildings.length
  const totalAssets = communityAssets.length
  const totalDatasets = communityDatasets.length

  // Check if this community has data uploaded
  const hasData = totalDatasets > 0

  // Count community-level asset types
  const pvAssets = communityAssets.filter(a => ['solar_pv_farm', 'ground_mounted_pv', 'pv'].includes(a.asset_type)).length
  const windAssets = communityAssets.filter(a => a.asset_type === 'wind_turbine').length
  const batteryAssets = communityAssets.filter(a => a.asset_type === 'community_battery' || a.asset_type === 'battery').length

  // Count building-level assets by type
  const buildingPvCount = buildings.reduce((sum, b) =>
    sum + (b.building_assets || []).filter(a => a.asset_type === 'rooftop_pv').length, 0)
  const buildingBattCount = buildings.reduce((sum, b) =>
    sum + (b.building_assets || []).filter(a => a.asset_type === 'battery').length, 0)
  const buildingOtherCount = buildings.reduce((sum, b) =>
    sum + (b.building_assets || []).filter(a => !['rooftop_pv', 'battery'].includes(a.asset_type)).length, 0)

  // Compute community-specific KPIs dynamically
  const kpis = useMemo(() => communityKPIs(community), [community])
  const currency = community?.currency || 'EUR'
  const isCZK = currency === 'CZK'

  // --- Real-data hooks (fetch from DB, fall back to synthetic) ---
  const { data: tariffProfileData } = useCommunityTariffProfile(id, community)
  const { data: energyBalanceResult } = useCommunityEnergyBalance(id, community)
  const { data: dbMonthlyBills } = useCommunityMonthlyBills(id, community, kpis)

  // Energy balance: use hook result (real or synthetic), or null if not ready
  // Switches between community-level and per-building data based on balanceScope
  const energyBalanceData = useMemo(() => {
    if (!hasData || !kpis) return null
    if (!energyBalanceResult) return null
    if (balanceScope && balanceScope !== 'community' && energyBalanceResult.perBuildingEB?.[balanceScope]) {
      return energyBalanceResult.perBuildingEB[balanceScope]
    }
    return energyBalanceResult
  }, [hasData, kpis, energyBalanceResult, balanceScope])

  // ---------------------------------------------------------------------------
  // Compute KPIs from real energy balance data when available (source='db').
  // Falls back to empirical communityKPIs() when no DB data exists.
  //
  // Definitions (IEA standard):
  //   Self-Consumption Rate (SCR) = selfConsumed / totalPV  ("% of generation used locally")
  //   Self-Sufficiency Rate (SSR) = selfConsumed / totalLoad ("% of load met by local generation")
  //   where selfConsumed = totalPV - totalSurplus = totalLoad - totalGridImport
  // ---------------------------------------------------------------------------
  const balanceKPIs = useMemo(() => {
    if (!energyBalanceResult || energyBalanceResult.source !== 'db') return null
    const { load, pv, gridImport, gridExport } = energyBalanceResult
    if (!load || !pv || load.length === 0) return null

    const totalLoad = load.reduce((s, v) => s + (v || 0), 0)
    const totalPV = pv.reduce((s, v) => s + (v || 0), 0)
    const totalGridImport = gridImport.reduce((s, v) => s + (v || 0), 0)
    const totalSurplus = gridExport.reduce((s, v) => s + (v || 0), 0)

    // Self-consumed = what we generated minus what we exported (surplus)
    const selfConsumed = Math.max(0, totalPV - totalSurplus)

    const scr = totalPV > 0 ? Math.round((selfConsumed / totalPV) * 100) : 0
    const ssr = totalLoad > 0 ? Math.round((selfConsumed / totalLoad) * 100) : 0

    // Daily cost from actual grid import and community tariff (with country fallback)
    const meta = community?.metadata || {}
    const countryRef = getCountryDefaults(community)
    const avgTariff = meta.import_tariff || countryRef.avgTariff || 0
    const dailyCost = +(totalGridImport * avgTariff).toFixed(1)

    // Yearly cost: scale daily cost by 365, adjusted by seasonal factor.
    // The energy balance is a seasonal average — apply a simple seasonal correction.
    const season = energyBalanceResult.sourceLabel?.match(/\((\w+)\)/)?.[1]?.toLowerCase()
    const seasonalCostFactor = {
      winter: 1.25,  // higher load, less PV
      spring: 0.95,
      summer: 0.80,  // lower load, more PV
      fall:   1.00,
    }
    const factor = seasonalCostFactor[season] || 1.0
    // Annual cost ~ daily cost * 365, but the current season is not representative
    // of the full year. Adjust: yearlyCost = dailyCost * 365 * (1/factor) * averageFactor
    // where averageFactor = mean of all seasonal factors = 1.0.
    // Simpler: yearlyCost = dailyCost * 365 (if we accept seasonal bias)
    // Better: adjust for seasonal representativeness
    const yearlyCost = Math.round(dailyCost * 365 / factor)

    return {
      selfConsumption: Math.min(scr, 99),
      selfSufficiency: Math.min(ssr, 99),
      dailyCost,
      yearlyCost,
    }
  }, [energyBalanceResult, kpis, community])

  // Energy balance source label for the badge
  const balanceSourceLabel = energyBalanceResult?.sourceLabel || t('community_dashboard.charts.last_24_hours')

  // Tariff chart: use hook result (real or synthetic)
  const tariffData = useMemo(() => {
    if (!hasData) return null
    return tariffProfileData || null
  }, [hasData, tariffProfileData])

  // Monthly bills: computed from real load data × tariffs. Null = empty chart.
  const monthlyBillsData = dbMonthlyBills || null

  // Format currency values for display
  const fmtCost = (val) => {
    if (!val && val !== 0) return '--'
    if (isCZK) {
      return val >= 10000 ? `${(val / 1000).toFixed(1)}k` : val.toFixed(0)
    }
    return val >= 10000 ? `${(val / 1000).toFixed(1)}k` : val.toFixed(1)
  }

  // Prefer real balance-derived KPIs over empirical model
  const effectiveKPIs = balanceKPIs || kpis

  // KPI data
  const buildingExtra = buildingPvCount + buildingBattCount + buildingOtherCount
  const kpiData = [
    {
      label: t('community_dashboard.kpi.self_consumption'),
      value: hasData && effectiveKPIs ? `${effectiveKPIs.selfConsumption}` : '--',
      unit: hasData ? '%' : '',
      sublabel: balanceKPIs ? t('community_dashboard.kpi.from_measured_data') : t('community_dashboard.kpi.of_local_generation_used'),
      color: 'var(--color-primary)',
      bgColor: 'var(--color-primary-pale)',
    },
    {
      label: t('community_dashboard.kpi.self_sufficiency'),
      value: hasData && effectiveKPIs ? `${effectiveKPIs.selfSufficiency}` : '--',
      unit: hasData ? '%' : '',
      sublabel: balanceKPIs ? t('community_dashboard.kpi.from_measured_data') : t('community_dashboard.kpi.of_load_met_locally'),
      color: 'var(--color-primary-light)',
      bgColor: '#f0f8e0',
    },
    {
      label: t('community_dashboard.kpi.daily_avg_cost'),
      value: hasData && effectiveKPIs ? fmtCost(effectiveKPIs.dailyCost) : '--',
      unit: hasData ? currency : '',
      sublabel: t('community_dashboard.kpi.grid_energy_cost_day'),
      color: 'var(--color-accent)',
      bgColor: '#FFF3E6',
    },
    {
      label: t('community_dashboard.kpi.yearly_avg_cost'),
      value: hasData && effectiveKPIs ? fmtCost(effectiveKPIs.yearlyCost) : '--',
      unit: hasData ? currency : '',
      sublabel: t('community_dashboard.kpi.projected_annual_cost'),
      color: 'var(--color-accent-dark)',
      bgColor: '#FFF0E0',
    },
    {
      label: t('community_dashboard.kpi.total_load'),
      value: totalBuildings > 0 ? `${totalBuildings} ${t('community_dashboard.kpi.bldg_suffix')}` : '--',
      unit: '',
      sublabel: t('community_dashboard.kpi.buildings_configured', { count: totalBuildings }),
      color: 'var(--color-load)',
      bgColor: '#FDEDEE',
    },
    {
      label: t('community_dashboard.kpi.community_assets'),
      value: totalAssets > 0 ? `${totalAssets}` : '--',
      unit: totalAssets > 0 ? t('community_dashboard.kpi.assets_suffix') : '',
      sublabel: t('community_dashboard.kpi.assets_breakdown_pv_wind_batt', { pv: pvAssets, wind: windAssets, batt: batteryAssets })
        + (buildingExtra > 0 ? t('community_dashboard.kpi.assets_building_suffix', { count: buildingExtra }) : ''),
      color: 'var(--color-solar)',
      bgColor: '#FFF9E6',
    },
  ]

  return (
    <div>
      {/* Page Header */}
      <div className="tool-page-header">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          <div>
            <h1 className="tool-page-title" style={{ fontSize: 'var(--font-size-2xl)' }}>
              {t('community_dashboard.title')}
            </h1>
            <p className="tool-page-subtitle">
              {t('community_dashboard.subtitle', { name: community?.name || t('community_dashboard.subtitle_fallback') })}
            </p>
          </div>
          <div className="d-flex gap-2">
            <Badge
              bg="none"
              style={{
                backgroundColor: hasData ? '#D1F2EB' : '#FEF3CD',
                color: hasData ? 'var(--color-success)' : 'var(--color-warning)',
                fontSize: 'var(--font-size-xs)',
                padding: '6px 12px',
              }}
            >
              {hasData ? t('community_dashboard.status_active') : t('community_dashboard.status_setup')}
            </Badge>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      <Row className="mb-4 g-3">
        {kpiData.map((kpi, index) => (
          <Col key={index} xs={12} sm={6} lg={4} xl={2}>
            <Card className="h-100 kpi-card shadow-sm">
              <div className="kpi-card-accent" style={{ backgroundColor: kpi.color }} />
              <Card.Body className="text-center py-3 px-2">
                <div className="kpi-value" style={{ color: kpi.color }}>
                  {kpi.value}
                  {kpi.unit && (
                    <span style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>
                      {' '}{kpi.unit}
                    </span>
                  )}
                </div>
                <div className="kpi-label">{kpi.label}</div>
                <div className="kpi-sublabel">{kpi.sublabel}</div>
              </Card.Body>
            </Card>
          </Col>
        ))}
      </Row>

      {/* Conditional: Charts or Empty State */}
      {hasData ? (
        <>
          {/* Charts Section */}
          <Row className="mb-4 g-3">
            <Col lg={8}>
              <Card className="shadow-sm" style={{ border: 'none', borderRadius: 'var(--radius-lg)' }}>
                <Card.Body>
                  <Card.Title
                    className="d-flex align-items-center justify-content-between mb-3"
                    style={{ color: 'var(--color-neutral-900)', fontSize: 'var(--font-size-base)' }}
                  >
                    <span>{t('community_dashboard.charts.energy_balance')}</span>
                    <Badge bg="secondary" style={{ fontWeight: 400, fontSize: '0.7rem' }}>{balanceSourceLabel}</Badge>
                  </Card.Title>
                  <EnergyBalanceChart
                    scope={balanceScope}
                    onScopeChange={setBalanceScope}
                    data={energyBalanceData}
                    buildings={[
                      { id: 'community', label: t('community_dashboard.charts.community_total') },
                      ...buildings.map(b => ({ id: b.id, label: `${b.building_id} - ${b.building_type || b.type}` })),
                    ]}
                  />
                </Card.Body>
              </Card>
            </Col>
            <Col lg={4}>
              <Card className="shadow-sm" style={{ border: 'none', borderRadius: 'var(--radius-lg)' }}>
                <Card.Body>
                  <Card.Title className="mb-3" style={{ color: 'var(--color-neutral-900)', fontSize: 'var(--font-size-base)' }}>
                    {t('community_dashboard.charts.tariff_profile')}
                  </Card.Title>
                  <TariffProfileChart
                    data={tariffData}
                    communityTariffs={community?.metadata ? {
                      importTariff: community.metadata.import_tariff || 0,
                      exportTariff: community.metadata.export_tariff || 0,
                    } : null}
                    currency={currency}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>

          <Row className="mb-4">
            <Col xs={12}>
              <Card className="shadow-sm" style={{ border: 'none', borderRadius: 'var(--radius-lg)' }}>
                <Card.Body>
                  <Card.Title className="mb-3" style={{ color: 'var(--color-neutral-900)', fontSize: 'var(--font-size-base)' }}>
                    {t('community_dashboard.charts.monthly_bills')}
                  </Card.Title>
                  <MonthlyBillsChart
                    data={monthlyBillsData}
                    currency={currency}
                    communityTariffs={community?.metadata ? {
                      importTariff: community.metadata.import_tariff || 0,
                    } : null}
                    buildings={buildings.map((b, i) => ({
                      id: b.id,
                      name: `${b.building_id} - ${b.building_type || b.type}`,
                      color: ['#8CB808', '#5C5C5C', '#6DB1BF', '#9B59B6', '#C73E1D', '#2E86AB', '#A23B72', '#F18F01'][i % 8],
                    }))}
                  />
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </>
      ) : (
        /* Empty State - No Data */
        <Card className="shadow-sm mb-4" style={{ border: 'none', borderRadius: 'var(--radius-lg)' }}>
          <Card.Body style={{ padding: 'var(--space-8)' }}>
            <div className="text-center" style={{ maxWidth: '500px', margin: '0 auto' }}>
              <div style={{ fontSize: '3rem', marginBottom: 'var(--space-4)' }}>&#x1F4CA;</div>
              <h4 style={{ color: 'var(--color-primary-dark)', fontWeight: 600, marginBottom: 'var(--space-3)' }}>
                {t('community_dashboard.empty.no_data_title')}
              </h4>
              <p style={{ color: 'var(--color-neutral-600)', fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-6)' }}>
                {t('community_dashboard.empty.no_data_subtitle')}
              </p>
              <div className="d-flex justify-content-center gap-3">
                <Link to={`/communities/${id}/data`}>
                  <Button style={{ backgroundColor: 'var(--color-primary)', borderColor: 'var(--color-primary)', fontWeight: 600 }}>
                    {t('community_dashboard.empty.upload_data')}
                  </Button>
                </Link>
                <Link to={`/communities/${id}/buildings`}>
                  <Button variant="outline-secondary">{t('community_dashboard.empty.manage_buildings')}</Button>
                </Link>
              </div>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Community Summary Row */}
      <Row className="g-3">
        {/* Buildings Summary */}
        <Col md={6} lg={4}>
          <Card className="shadow-sm h-100" style={{ border: 'none', borderRadius: 'var(--radius-lg)' }}>
            <Card.Body>
              <Card.Title
                className="d-flex align-items-center justify-content-between"
                style={{ color: 'var(--color-neutral-900)', fontSize: 'var(--font-size-base)' }}
              >
                <span>{t('community_dashboard.summary.buildings')}</span>
                <Badge bg="none" style={{ backgroundColor: 'var(--color-primary-pale)', color: 'var(--color-primary-dark)' }}>
                  {totalBuildings}
                </Badge>
              </Card.Title>
              {totalBuildings === 0 ? (
                <p style={{ color: 'var(--color-neutral-500)', fontSize: 'var(--font-size-sm)' }}>
                  {t('community_dashboard.summary.no_buildings')}
                </p>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {buildings.slice(0, 5).map((b, i) => (
                    <div key={b.id || i} className="d-flex align-items-center justify-content-between"
                      style={{ padding: 'var(--space-2) 0', borderBottom: '1px solid var(--color-neutral-100)' }}>
                      <div>
                        <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>{b.building_id}</div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>
                          {b.building_type || b.type} | {b.floor_area_m2 || '--'} m2
                        </div>
                      </div>
                      <div>
                        {b.building_assets?.length > 0 && (
                          <Badge bg="none" style={{ backgroundColor: '#f0f8e0', color: 'var(--color-primary)', fontSize: '0.65rem' }}>
                            {t('community_dashboard.summary.n_assets', { count: b.building_assets.length })}
                          </Badge>
                        )}
                      </div>
                    </div>
                  ))}
                  {totalBuildings > 5 && (
                    <Link to={`/communities/${id}/buildings`}
                      style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-primary)' }}>
                      {t('community_dashboard.summary.view_all_buildings', { count: totalBuildings })}
                    </Link>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Community Assets Summary */}
        <Col md={6} lg={4}>
          <Card className="shadow-sm h-100" style={{ border: 'none', borderRadius: 'var(--radius-lg)' }}>
            <Card.Body>
              <Card.Title
                className="d-flex align-items-center justify-content-between"
                style={{ color: 'var(--color-neutral-900)', fontSize: 'var(--font-size-base)' }}
              >
                <span>{t('community_dashboard.summary.community_assets')}</span>
                <Badge bg="none" style={{ backgroundColor: 'var(--color-primary-pale)', color: 'var(--color-primary-dark)' }}>
                  {totalAssets}
                </Badge>
              </Card.Title>
              {totalAssets === 0 ? (
                <p style={{ color: 'var(--color-neutral-500)', fontSize: 'var(--font-size-sm)' }}>
                  {t('community_dashboard.summary.no_assets')}
                </p>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {communityAssets.map((asset, i) => {
                    const typeColors = {
                      solar_pv_farm: { bg: '#FFF9E6', color: 'var(--color-solar)', icon: '\u2600' },
                      ground_mounted_pv: { bg: '#FFF9E6', color: 'var(--color-solar)', icon: '\u2600' },
                      pv: { bg: '#FFF9E6', color: 'var(--color-solar)', icon: '\u2600' },
                      wind_turbine: { bg: '#EBF2FC', color: 'var(--color-wind)', icon: '\uD83D\uDCA8' },
                      community_battery: { bg: '#F3EBF8', color: 'var(--color-battery)', icon: '\uD83D\uDD0B' },
                      battery: { bg: '#F3EBF8', color: 'var(--color-battery)', icon: '\uD83D\uDD0B' },
                    }
                    const typeConfig = typeColors[asset.asset_type] || { bg: '#f5f5f5', color: '#666', icon: '\u26A1' }

                    return (
                      <div key={asset.id || i} className="asset-summary-card">
                        <div className="asset-summary-icon" style={{ backgroundColor: typeConfig.bg, color: typeConfig.color }}>
                          {typeConfig.icon}
                        </div>
                        <div className="asset-summary-info">
                          <p className="asset-summary-name">{asset.name || asset.asset_type}</p>
                          <p className="asset-summary-detail">
                            {asset.specs?.capacity_kwp && `${asset.specs.capacity_kwp} kWp`}
                            {asset.specs?.capacity_kw && `${asset.specs.capacity_kw} kW`}
                            {asset.specs?.capacity_kwh && `${asset.specs.capacity_kwh} kWh`}
                          </p>
                        </div>
                      </div>
                    )
                  })}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>

        {/* Datasets Summary */}
        <Col md={12} lg={4}>
          <Card className="shadow-sm h-100" style={{ border: 'none', borderRadius: 'var(--radius-lg)' }}>
            <Card.Body>
              <Card.Title
                className="d-flex align-items-center justify-content-between"
                style={{ color: 'var(--color-neutral-900)', fontSize: 'var(--font-size-base)' }}
              >
                <span>{t('community_dashboard.summary.datasets')}</span>
                <Badge bg="none" style={{ backgroundColor: 'var(--color-primary-pale)', color: 'var(--color-primary-dark)' }}>
                  {totalDatasets}
                </Badge>
              </Card.Title>
              {totalDatasets === 0 ? (
                <div>
                  <p style={{ color: 'var(--color-neutral-500)', fontSize: 'var(--font-size-sm)' }}>
                    {t('community_dashboard.summary.no_datasets')}
                  </p>
                  <Link to={`/communities/${id}/data`}
                    style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-primary)', fontWeight: 500 }}>
                    {t('community_dashboard.summary.upload_first')}
                  </Link>
                </div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {communityDatasets.slice(0, 5).map((ds, i) => (
                    <div key={ds.id || i}
                      style={{
                        padding: 'var(--space-2) 0',
                        borderBottom: '1px solid var(--color-neutral-100)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                      }}>
                      <div>
                        <div style={{ fontSize: 'var(--font-size-sm)', fontWeight: 500 }}>{ds.name}</div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-neutral-500)' }}>
                          {ds.dataset_type || ds.kind}
                        </div>
                      </div>
                    </div>
                  ))}
                  {totalDatasets > 5 && (
                    <Link to={`/communities/${id}/data`}
                      style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-primary)' }}>
                      {t('community_dashboard.summary.view_all_datasets', { count: totalDatasets })}
                    </Link>
                  )}
                </div>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Info Banner */}
      <Row className="mt-4">
        <Col>
          <Card
            className="shadow-sm"
            style={{
              backgroundColor: 'var(--color-primary-pale)',
              border: 'none',
              borderRadius: 'var(--radius-md)',
              borderLeft: '4px solid var(--color-primary)',
            }}
          >
            <Card.Body className="py-3">
              <div className="d-flex align-items-center gap-3">
                <span style={{ fontSize: '1.5rem' }}>&#x1F4A1;</span>
                <div>
                  <div style={{ fontWeight: 600, color: 'var(--color-primary-dark)', fontSize: 'var(--font-size-sm)' }}>
                    {hasData ? t('community_dashboard.banner.ready_title') : t('community_dashboard.banner.getting_started_title')}
                  </div>
                  <div style={{ color: 'var(--color-neutral-700)', fontSize: 'var(--font-size-sm)' }}>
                    {hasData ? (
                      <>
                        {t('community_dashboard.banner.ready_prefix')}
                        <Link to={`/communities/${id}/modelling`} style={{ color: 'var(--color-primary)', fontWeight: 500 }}>
                          {t('community_dashboard.banner.ready_link')}
                        </Link>
                        {t('community_dashboard.banner.ready_suffix')}
                      </>
                    ) : (
                      <>
                        {t('community_dashboard.banner.getting_started_prefix')}
                        <Link to={`/communities/${id}/data`} style={{ color: 'var(--color-primary)', fontWeight: 500 }}>
                          {t('community_dashboard.banner.getting_started_link')}
                        </Link>
                        {t('community_dashboard.banner.getting_started_suffix')}
                      </>
                    )}
                  </div>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </div>
  )
}

export default CommunityDashboard
